// Builds an editable .docx mirror of the Redemption Partners 1-pager.
//
// The output is loaded with the same live-aware numbers the in-app surface
// renders, so the downloaded file is the source of truth at the moment of
// export. Teams open this in Word / Google Docs and edit further; we do
// NOT re-import edits — the in-app surface stays driven by code.
//
// Layout decisions:
//   - A4 portrait, 1cm margins to match the in-app surface density.
//   - Header bar in Bvndle purple (#534AB7).
//   - Four headline tiles laid out as a 4-column table with shaded big
//     numbers (Word doesn't have native flex tiles; tables ARE the layout
//     primitive in docx).
//   - All numerical cells right-aligned with tabular-nums equivalent
//     (Calibri's lining figures align well enough).
//   - Section headings prefixed "01 / 02 / 03" to match the source.
//
// docx-js note: every Paragraph and Table needs to be a fresh instance
// per insertion — sharing instances breaks the OOXML serialiser.

import {
  AlignmentType,
  BorderStyle,
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
  type ISectionOptions,
} from "docx";

// docx exports AlignmentType as a const object, so we need this to use it
// as a TS type. Aliased once and used in interface fields below.
type AlignmentValue = (typeof AlignmentType)[keyof typeof AlignmentType];

import { type LiveState } from "./live-category-stats";
import { type Merchant, type CategoryRollup } from "./partners-current";

// Brand colours mirrored from the in-app surface.
const PURPLE = "534AB7";
const INK = "1A1A1A";
const INK_SOFT = "3A3A3A";
const INK_FAINT = "6B6B6B";
const LINE = "D9D9D4";
const LINE_SOFT = "EBEAE5";
const PAPER = "FAFAF7";
const PRIMARY = "B8860B"; // star colour
const SHADE = "F1F0EB";

export interface OnePagerData {
  generatedOn: string;
  sourceDate: string;
  liveState: LiveState;
  tiles: {
    merchantsTargeted: number;
    ytdRedemption: number;
    priorityMerchants: number;
    categoryCount: number;
  };
  overviewCompletion: number;
  priorityShare: { value: number; pct: number };
  rollups: CategoryRollup[];
  effectivePerCategory: Record<string, number>;
  monthlyMerchants: Merchant[];
  monthlyTotals: { jan: number; feb: number; mar: number; apr: number; may: number; ytd: number };
  selectionCriteria: Array<{ title: string; description: string; starred?: boolean }>;
}

export async function buildOnePagerDocx(data: OnePagerData): Promise<Blob> {
  const section: ISectionOptions = {
    properties: {
      page: {
        margin: { top: 720, right: 720, bottom: 720, left: 720 }, // 0.5 in
      },
    },
    children: [
      ...header(data),
      blank(),
      tilesTable(data),
      blank(),
      ...overview(data),
      blank(),
      ...sectionTitle("01", "Category of Redemption Merchants"),
      sectionBlurb(
        `Merchants are grouped into ${data.rollups.length} categories. ` +
          `The Retail (Supermarkets) category is the primary anchor — ` +
          `everyday essential spending.`,
      ),
      categoryTable(data),
      blank(),
      ...sectionTitle("02", "Criteria for Merchant Selection"),
      criteriaTable(data),
      blank(),
      ...sectionTitle("03", "Redemption Value · Month-on-Month"),
      sectionBlurb(
        `Merchant redemption details · coins redeemed per merchant · ` +
          `January – May 2026 · successful credit transactions only.`,
      ),
      merchantTable(data),
      blank(),
      footer(),
    ],
  };

  const doc = new Document({
    creator: "Bvndle Command Centre",
    title: "Bvndle Redemption Partners 1-pager",
    description: `Generated ${data.generatedOn} · snapshot ${data.sourceDate}`,
    styles: {
      default: {
        document: {
          run: { font: "Calibri", size: 20 }, // 10pt
        },
      },
    },
    sections: [section],
  });

  return Packer.toBlob(doc);
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function header(data: OnePagerData): Paragraph[] {
  return [
    new Paragraph({
      children: [
        new TextRun({
          text: "BVNDLE · MANCO",
          color: INK_FAINT,
          size: 16, // 8pt
          bold: true,
          characterSpacing: 30,
        }),
      ],
    }),
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      children: [
        new TextRun({
          text: "Redemption Partners Report",
          color: INK,
          bold: true,
          size: 44, // 22pt
        }),
      ],
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `${data.generatedOn}  ·  Snapshot ${data.sourceDate}  ·  ` +
            `Live state: ${data.liveState.toUpperCase()}`,
          color: INK_FAINT,
          size: 16, // 8pt
        }),
      ],
      border: {
        bottom: { style: BorderStyle.SINGLE, size: 8, color: INK, space: 4 },
      },
    }),
  ];
}

// ---------------------------------------------------------------------------
// Tiles (rendered as a 4-column table)
// ---------------------------------------------------------------------------

function tilesTable(data: OnePagerData): Table {
  const tiles = [
    { big: String(data.tiles.merchantsTargeted), label: "Merchants Targeted" },
    {
      big: formatNum(data.tiles.ytdRedemption),
      label: "Coins Redeemed (YTD)",
      note: data.liveState === "live" ? "LIVE" : `Snapshot ${data.sourceDate}`,
    },
    { big: String(data.tiles.priorityMerchants), label: "Priority Merchants" },
    { big: String(data.tiles.categoryCount), label: "Merchant Categories" },
  ];

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      new TableRow({
        children: tiles.map((t) => tileCell(t.big, t.label, t.note)),
      }),
    ],
  });
}

function tileCell(big: string, label: string, note?: string): TableCell {
  return new TableCell({
    width: { size: 25, type: WidthType.PERCENTAGE },
    shading: { type: ShadingType.CLEAR, color: "auto", fill: "FFFFFF" },
    margins: { top: 120, bottom: 120, left: 160, right: 160 },
    borders: borderAll(LINE),
    children: [
      new Paragraph({
        children: [
          new TextRun({
            text: big,
            color: PURPLE,
            bold: true,
            size: 44, // 22pt
          }),
        ],
      }),
      new Paragraph({
        children: [
          new TextRun({
            text: label.toUpperCase(),
            color: INK_SOFT,
            bold: true,
            size: 14, // 7pt
            characterSpacing: 20,
          }),
        ],
      }),
      ...(note
        ? [
            new Paragraph({
              children: [
                new TextRun({
                  text: note,
                  color: INK_FAINT,
                  size: 12, // 6pt
                  characterSpacing: 20,
                }),
              ],
            }),
          ]
        : []),
    ],
  });
}

// ---------------------------------------------------------------------------
// Overview paragraphs
// ---------------------------------------------------------------------------

function overview(data: OnePagerData): Paragraph[] {
  const completion = (data.overviewCompletion * 100).toFixed(0);
  const onboarded = data.rollups.reduce((s, r) => s + r.onboardedCount, 0);
  const target = data.rollups.reduce(
    (s, r) => s + r.category.targetOnboarded,
    0,
  );
  const sharePct = (data.priorityShare.pct * 100).toFixed(0);

  return [
    headingMd("Overview"),
    new Paragraph({
      spacing: { after: 120 },
      children: [
        plain("While overall onboarding has reached "),
        bold(`${completion}% `),
        plain(
          `of target (${onboarded} of ${target} slots), the programme has ` +
            `secured coverage in core spending categories. The immediate ` +
            `priority is completing merchant acquisition in underrepresented ` +
            `lifestyle and experiential categories to achieve a balanced ` +
            `and commercially compelling ecosystem.`,
        ),
      ],
    }),
    new Paragraph({
      children: [
        plain("Priority merchants contributed "),
        bold(`${sharePct}% (${formatNum(data.priorityShare.value)} coins) `),
        plain(
          "of total coin value generated between January and May, " +
            "underscoring their role as the primary drivers of ecosystem " +
            "value. Their outsized contribution validates the strategic " +
            "focus on high-impact merchant partnerships and highlights the " +
            "opportunity to further accelerate growth through targeted " +
            "merchant acquisition, activation, and retention initiatives.",
        ),
      ],
    }),
  ];
}

// ---------------------------------------------------------------------------
// Section heading helpers
// ---------------------------------------------------------------------------

function sectionTitle(num: string, title: string): Paragraph[] {
  return [
    new Paragraph({
      spacing: { before: 200, after: 80 },
      border: {
        bottom: { style: BorderStyle.SINGLE, size: 4, color: LINE, space: 4 },
      },
      children: [
        new TextRun({
          text: `${num}  `,
          color: PURPLE,
          bold: true,
          size: 22, // 11pt
        }),
        new TextRun({
          text: title,
          color: INK,
          bold: true,
          size: 24, // 12pt
        }),
      ],
    }),
  ];
}

function sectionBlurb(text: string): Paragraph {
  return new Paragraph({
    spacing: { after: 80 },
    children: [new TextRun({ text, color: INK_SOFT, size: 18 })], // 9pt
  });
}

function headingMd(text: string): Paragraph {
  return new Paragraph({
    spacing: { before: 100, after: 80 },
    border: {
      bottom: { style: BorderStyle.SINGLE, size: 4, color: LINE, space: 4 },
    },
    children: [
      new TextRun({ text, color: INK, bold: true, size: 24 }), // 12pt
    ],
  });
}

// ---------------------------------------------------------------------------
// Section 01 — Category table
// ---------------------------------------------------------------------------

function categoryTable(data: OnePagerData): Table {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: borderAll(LINE_SOFT),
    rows: [
      headerRow([
        { text: "Category", width: 22 },
        { text: "Description / Focus", width: 41 },
        { text: "Onboarded", width: 11, align: AlignmentType.RIGHT },
        { text: "Completion", width: 12, align: AlignmentType.RIGHT },
        { text: "YTD (coins)", width: 14, align: AlignmentType.RIGHT },
      ]),
      ...data.rollups.map((r) =>
        bodyRow([
          {
            text: `${r.category.primary ? "★ " : ""}${r.category.label}`,
            bold: true,
            starColor: r.category.primary ? PRIMARY : undefined,
          },
          { text: r.category.description, color: INK_SOFT },
          {
            text: `${r.onboardedCount} / ${r.category.targetOnboarded}`,
            align: AlignmentType.RIGHT,
          },
          {
            text: `${(r.completion * 100).toFixed(0)}%`,
            align: AlignmentType.RIGHT,
          },
          {
            text: formatNum(
              data.effectivePerCategory[r.category.code] ?? 0,
            ),
            align: AlignmentType.RIGHT,
          },
        ]),
      ),
    ],
  });
}

// ---------------------------------------------------------------------------
// Section 02 — Criteria table
// ---------------------------------------------------------------------------

function criteriaTable(data: OnePagerData): Table {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: borderAll(LINE_SOFT),
    rows: [
      headerRow([
        { text: "Selection Criterion", width: 26 },
        { text: "Description", width: 74 },
      ]),
      ...data.selectionCriteria.map((c) =>
        bodyRow([
          {
            text: `${c.starred ? "★ " : ""}${c.title}`,
            bold: true,
            starColor: c.starred ? PRIMARY : undefined,
          },
          { text: c.description, color: INK_SOFT },
        ]),
      ),
    ],
  });
}

// ---------------------------------------------------------------------------
// Section 03 — Merchant monthly table
// ---------------------------------------------------------------------------

function merchantTable(data: OnePagerData): Table {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: borderAll(LINE_SOFT),
    rows: [
      headerRow([
        { text: "S/N", width: 4, align: AlignmentType.RIGHT },
        { text: "Merchant", width: 36 },
        { text: "Jan", width: 9, align: AlignmentType.RIGHT },
        { text: "Feb", width: 9, align: AlignmentType.RIGHT },
        { text: "Mar", width: 9, align: AlignmentType.RIGHT },
        { text: "Apr", width: 9, align: AlignmentType.RIGHT },
        { text: "May", width: 9, align: AlignmentType.RIGHT },
        { text: "Total (YTD)", width: 15, align: AlignmentType.RIGHT },
      ]),
      ...data.monthlyMerchants.map((m, i) =>
        bodyRow([
          { text: String(i + 1), align: AlignmentType.RIGHT },
          { text: m.name },
          { text: fmtMonthCell(m.monthly?.jan), align: AlignmentType.RIGHT },
          { text: fmtMonthCell(m.monthly?.feb), align: AlignmentType.RIGHT },
          { text: fmtMonthCell(m.monthly?.mar), align: AlignmentType.RIGHT },
          { text: fmtMonthCell(m.monthly?.apr), align: AlignmentType.RIGHT },
          { text: fmtMonthCell(m.monthly?.may), align: AlignmentType.RIGHT },
          {
            text: formatNum(m.ytdTotal ?? 0),
            align: AlignmentType.RIGHT,
            bold: true,
          },
        ]),
      ),
      // Grand total
      new TableRow({
        children: [
          { text: "", shade: true },
          { text: "GRAND TOTAL", bold: true, shade: true },
          {
            text: formatNum(data.monthlyTotals.jan),
            align: AlignmentType.RIGHT,
            bold: true,
            shade: true,
          },
          {
            text: formatNum(data.monthlyTotals.feb),
            align: AlignmentType.RIGHT,
            bold: true,
            shade: true,
          },
          {
            text: formatNum(data.monthlyTotals.mar),
            align: AlignmentType.RIGHT,
            bold: true,
            shade: true,
          },
          {
            text: formatNum(data.monthlyTotals.apr),
            align: AlignmentType.RIGHT,
            bold: true,
            shade: true,
          },
          {
            text: formatNum(data.monthlyTotals.may),
            align: AlignmentType.RIGHT,
            bold: true,
            shade: true,
          },
          {
            text: formatNum(data.monthlyTotals.ytd),
            align: AlignmentType.RIGHT,
            bold: true,
            shade: true,
          },
        ].map((c) =>
          cellOf({
            text: c.text,
            align: ("align" in c ? c.align : undefined) as AlignmentValue | undefined,
            bold: ("bold" in c ? c.bold : undefined) as boolean | undefined,
            shade: true,
          }),
        ),
      }),
    ],
  });
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function footer(): Paragraph {
  return new Paragraph({
    spacing: { before: 200 },
    border: {
      top: { style: BorderStyle.SINGLE, size: 4, color: LINE, space: 4 },
    },
    children: [
      new TextRun({
        text: "BVNDLE COMMAND CENTRE      ·      POWERED BY VFD GROUP",
        color: INK_FAINT,
        size: 14, // 7pt
        bold: true,
        characterSpacing: 30,
      }),
    ],
  });
}

// ---------------------------------------------------------------------------
// Shared cell + row helpers
// ---------------------------------------------------------------------------

interface HeaderCellSpec {
  text: string;
  width: number;
  align?: AlignmentValue;
}

function headerRow(specs: HeaderCellSpec[]): TableRow {
  return new TableRow({
    tableHeader: true,
    children: specs.map(
      (s) =>
        new TableCell({
          width: { size: s.width, type: WidthType.PERCENTAGE },
          shading: { type: ShadingType.CLEAR, color: "auto", fill: SHADE },
          margins: { top: 60, bottom: 60, left: 80, right: 80 },
          borders: borderAll(LINE_SOFT),
          children: [
            new Paragraph({
              alignment: s.align,
              children: [
                new TextRun({
                  text: s.text.toUpperCase(),
                  color: INK,
                  bold: true,
                  size: 14, // 7pt
                  characterSpacing: 20,
                }),
              ],
            }),
          ],
        }),
    ),
  });
}

interface BodyCellSpec {
  text: string;
  align?: AlignmentValue;
  bold?: boolean;
  color?: string;
  shade?: boolean;
  starColor?: string;
}

function bodyRow(specs: BodyCellSpec[]): TableRow {
  return new TableRow({
    children: specs.map((s) => cellOf(s)),
  });
}

function cellOf(s: BodyCellSpec): TableCell {
  return new TableCell({
    shading: s.shade
      ? { type: ShadingType.CLEAR, color: "auto", fill: SHADE }
      : undefined,
    margins: { top: 60, bottom: 60, left: 80, right: 80 },
    borders: borderAll(LINE_SOFT),
    children: [
      new Paragraph({
        alignment: s.align,
        children: [
          new TextRun({
            text: s.text,
            color: s.color ?? INK,
            bold: s.bold,
            size: 18, // 9pt
          }),
        ],
      }),
    ],
  });
}

function borderAll(color: string) {
  return {
    top: { style: BorderStyle.SINGLE, size: 2, color },
    bottom: { style: BorderStyle.SINGLE, size: 2, color },
    left: { style: BorderStyle.SINGLE, size: 2, color },
    right: { style: BorderStyle.SINGLE, size: 2, color },
  };
}

function blank(): Paragraph {
  return new Paragraph({ children: [new TextRun({ text: "" })] });
}

function plain(text: string): TextRun {
  return new TextRun({ text, color: INK_SOFT, size: 20 });
}

function bold(text: string): TextRun {
  return new TextRun({ text, color: INK, bold: true, size: 20 });
}

function formatNum(n: number): string {
  return n.toLocaleString("en-GB");
}

function fmtMonthCell(n: number | undefined): string {
  if (n == null || n === 0) return "—";
  return formatNum(n);
}

// Silence the linter for the unused PAPER constant (kept for parity with the
// in-app CSS palette and future surface tweaks).
void PAPER;
